import React, { useEffect, useMemo, useState } from "react";
import "./KnowYourBenefitsForm.css";
import {
  fetchBeneficiaryCategoryOptions,
  fetchBenefitFeeDetails,
  fetchBenefitsCalculation,
  fetchCasteCategoryOptions,
  fetchCollegeById,
  fetchCollegeOptionsByTaluka,
  fetchCourseTypePicklist,
  fetchCoursesForCollegeId,
  fetchDepartmentOptions,
  fetchDivisionNameByDistrictCode,
  fetchDistrictOptionsByState,
  fetchGenderOptions,
  fetchInstituteTypeOptions,
  fetchReligionOptions,
  fetchSchemeConfiguratorOptions,
  fetchSchemeTypeOptions,
  fetchStateOptions,
  fetchTalukaOptionsByDistrict,
} from "../api/masterData";

const initialForm = {
  departmentName: "",
  schemeName: "",
  incomeSlab: "",
  casteCategory: "",
  beneficiaryCategory: "",
  gender: "",
  religion: "",
  courseType: "",
  instituteType: "",
  instituteLocation: "",
  districtId: "",
  talukaId: "",
  collegeId: "",
  courseId: "",
};

const BENEFIT_SECTION_LABELS = {
  feeBenefits: "Fee Benefits",
  additionalBenefits: "Additional Benefits",
  totalAmount: "Total Amount",
  installments: "Installments",
};

const isPlainObject = (value) =>
  Object.prototype.toString.call(value) === "[object Object]";

const calculateBenefitFallbackTotal = (feeBenefits = {}, additionalBenefits = {}) => {
  const sumEntries = (entries = []) =>
    entries.reduce((sum, [label, value]) => {
      if (String(label).toLowerCase().includes("percentage")) {
        return sum;
      }

      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? sum + numericValue : sum;
    }, 0);

  return (
    sumEntries(Object.entries(feeBenefits)) +
    sumEntries(Object.entries(additionalBenefits))
  );
};

const normalizeBenefitsData = (benefitsData) => {
  if (!isPlainObject(benefitsData)) {
    return null;
  }

  const feeBenefits = isPlainObject(benefitsData.feeBenefits)
    ? benefitsData.feeBenefits
    : {};

  const additionalBenefits = isPlainObject(benefitsData.additionalBenefits)
    ? benefitsData.additionalBenefits
    : {};

  const installments = isPlainObject(benefitsData.installments)
    ? benefitsData.installments
    : {};

  const totalAmount =
    benefitsData.totalAmount !== undefined &&
    benefitsData.totalAmount !== null &&
    benefitsData.totalAmount !== ""
      ? benefitsData.totalAmount
      : calculateBenefitFallbackTotal(feeBenefits, additionalBenefits);

  return {
    feeBenefits,
    additionalBenefits,
    totalAmount,
    installments,
  };
};

function formatBenefitLabel(key) {
  return String(key || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatBenefitNumber(value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return value ?? "N/A";
  }

  return numericValue.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatBenefitValue(key, value) {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  if (String(key).toLowerCase().includes("percentage")) {
    return `${formatBenefitNumber(value)}%`;
  }

  if (!Number.isNaN(Number(value))) {
    return `Rs. ${formatBenefitNumber(value)}`;
  }

  return value;
}

function getSelectedOptionLabel(options = [], value) {
  return (
    (Array.isArray(options) ? options : []).find(
      (item) => String(item?.value || "") === String(value || "")
    )?.label || ""
  );
}

function isProfessionalFromCourseValue(value) {
  return (
    value === true ||
    value === 1 ||
    String(value === null || value === undefined ? "" : value).trim() === "1"
  );
}

function getCourseTypeLabelFromCourse(course) {
  const rawValue = course?.isProfessional;

  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return "";
  }

  return isProfessionalFromCourseValue(rawValue)
    ? "Professional"
    : "Non Professional";
}

function getInstituteTypeLabelFromCollege(college) {
  return String(college?.collegeType || "").trim();
}

function formatCollegeDisplayLabel(college) {
  const collegeName = String(college?.label || "").trim();
  const departmentCode = String(college?.departmentCode || "").trim();

  if (!departmentCode) {
    return collegeName;
  }

  return `(${departmentCode}) - ${collegeName}`;
}

function KnowYourBenefitsForm() {
  const [schemeOptions, setSchemeOptions] = useState([]);
  const [schemeLoading, setSchemeLoading] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [benefitsResult, setBenefitsResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedDrl, setSelectedDrl] = useState("");
  const [options, setOptions] = useState({
    departments: [],
    schemes: [],
    schemeTypes: [],
    casteCategories: [],
    beneficiaryCategories: [],
    genders: [],
    religions: [],
    courseTypes: [],
    instituteTypes: [],
    instituteLocations: [],
    districts: [],
    talukas: [],
    colleges: [],
    courses: [],
  });
  const [loading, setLoading] = useState({
    initial: true,
    districts: false,
    talukas: false,
    colleges: false,
    courses: false,
  });
  const [loadError, setLoadError] = useState("");
  const [submitError, setSubmitError] = useState("");
  function unescapeString(str) {
    try {
      return JSON.parse(`"${str}"`);
    } catch {
      return str;
    }
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setSubmitError("");

    setFormData((current) => {
      const nextState = {
        ...current,
        [name]: value,
      };

      if (name === "departmentName") {
        nextState.schemeName = "";
        nextState.collegeId = "";
        nextState.courseId = "";
        nextState.courseType = "";
        nextState.instituteType = "";
        setSelectedDrl("");
      }

      if (name === "instituteLocation") {
        nextState.districtId = "";
        nextState.talukaId = "";
        nextState.collegeId = "";
        nextState.courseId = "";
        nextState.courseType = "";
        nextState.instituteType = "";
      }

      if (name === "districtId") {
        nextState.talukaId = "";
        nextState.collegeId = "";
        nextState.courseId = "";
        nextState.courseType = "";
        nextState.instituteType = "";
      }

      if (name === "talukaId") {
        nextState.collegeId = "";
        nextState.courseId = "";
        nextState.courseType = "";
        nextState.instituteType = "";
      }

      if (name === "collegeId") {
        const selectedCollege = (options.colleges || []).find(
          (item) => String(item?.value || "") === String(value || "")
        );
        nextState.courseId = "";
        nextState.courseType = "";
        nextState.instituteType = getInstituteTypeLabelFromCollege(selectedCollege);
      }

      if (name === "courseId") {
        const selectedCourse = (options.courses || []).find(
          (item) => String(item?.value || "") === String(value || "")
        );
        nextState.courseType = getCourseTypeLabelFromCourse(selectedCourse);
      }

      return nextState;
    });
    if (name === "schemeName") {
      setSelectedDrl("");
    }
  };

  const handleReset = () => {
    setFormData(initialForm);
    setSelectedDrl("");
    setBenefitsResult(null);
    setShowModal(false);
    setSubmitError("");
  };
  const handleSubmitBenefits = async () => {
    try {
      setSubmitError("");
      console.log("Form Data ::: ", formData);

      let hostlerData = formData.beneficiaryCategory;
      const isHosteler = hostlerData === "Hosteller";
      const selectedDistrictOption = (options.districts || []).find(
        (district) => String(district?.value || "") === String(formData.districtId || "")
      );
      const districtCode = String(selectedDistrictOption?.districtCode || "").trim();
      const [selectedCollege, feeDetails, latestSchemeOptions, divisionName] = await Promise.all([
        fetchCollegeById(formData.collegeId),
        fetchBenefitFeeDetails(formData.collegeId, formData.courseId),
        formData.departmentName
          ? fetchSchemeConfiguratorOptions(formData.departmentName)
          : Promise.resolve([]),
        districtCode ? fetchDivisionNameByDistrictCode(districtCode) : Promise.resolve(""),
      ]);

      const locationLabel = getSelectedOptionLabel(
        options.instituteLocations,
        formData.instituteLocation
      );
      const districtLabel = getSelectedOptionLabel(
        options.districts,
        formData.districtId
      );
      const talukaLabel = getSelectedOptionLabel(
        options.talukas,
        formData.talukaId
      );
      const categoryLabel = getSelectedOptionLabel(
        options.casteCategories,
        formData.casteCategory
      );
      const annualIncome = Number(formData.incomeSlab) || 0;
      const selectedCollegeOption = (options.colleges || []).find(
        (college) => String(college?.value || "") === String(formData.collegeId || "")
      );
      const instituteType =
        String(selectedCollege?.collegeType || "").trim() ||
        formData.instituteType ||
        "";
      const instituteRegionId =
        Number(
          selectedCollege?.instituteRegionID ??
            selectedCollege?.instituteRegionId ??
            selectedCollegeOption?.instituteRegionId
        ) || "";
      const selectedCourse = (options.courses || []).find(
        (course) => String(course?.value || "") === String(formData.courseId || "")
      );
      const instituteName = String(
        selectedCollege?.name ||
          selectedCollege?.instituteName ||
          selectedCollegeOption?.label ||
          ""
      ).trim();
      const courseName = String(
        selectedCourse?.courseName ||
          // selectedCourse?.programName ||
          selectedCourse?.name ||
          selectedCourse?.label ||
          ""
      ).trim();
      const streamName = String(selectedCourse?.streamName || "").trim();
      const sjsaGroupingDisability = String(
        selectedCourse?.sjsaGroupingDisability ?? ""
      ).trim();
      const courseGroupingTribal = String(
        selectedCourse?.courseGroupingTribal ?? ""
      ).trim();
      const courseGroupingSC = String(
        selectedCourse?.courseGroupingSC ?? ""
      ).trim();
      const courseGroupingVjnt = String(
        selectedCourse?.courseGroupingVjnt ?? ""
      ).trim();
      const isProfessionalCourse =
        selectedCourse?.isProfessional === true ||
        selectedCourse?.isProfessional === 1 ||
        String(selectedCourse?.isProfessional || "").trim() === "1";

      console.log("location :::: ", locationLabel)
      console.log("category :::::: ", categoryLabel)
      const latestDrl = unescapeString(
        (latestSchemeOptions || []).find(
          (item) => String(item?.value || "") === String(formData.schemeName || "")
        )?.drl || ""
      );
      setSchemeOptions(latestSchemeOptions || []);
      setSelectedDrl(latestDrl);
      const activeDrl = latestDrl?.trim();

      if (!activeDrl?.trim()) {
        setBenefitsResult(null);
        setShowModal(false);
        setSubmitError("Benefits not available for the selected scheme.");
        return;
      }

      console.log("selectedDrl ", selectedDrl);
      console.log("activeDrl ", activeDrl);
      const payload = {
        student: {
          gender: formData.gender || "",
          religion: formData.religion || "",
          familyIncome: annualIncome,
          annualIncome,
          professionalCourse: isProfessionalCourse,
          hosteler: isHosteler,
          registeredLabor: false,
          marginalFarmer: false,
          metroLocation: false,
          openEws: false,
          category: categoryLabel || "",
          courseCategoryName: formData.courseType || "",
          instituteType,
          instituteRegionId,
          instituteName,
          courseName,
          streamName,
          sjsaGroupingDisability,
          courseGroupingTribal,
          courseGroupingSC,
          courseGroupingVjnt,
          divisionName: String(divisionName || "").trim(),
          location: locationLabel || "",
          district: districtLabel || "",
          taluka: talukaLabel || "",
          examFee: Number(feeDetails?.examFee) || "",
          tuitionFee: Number(feeDetails?.tuitionFee) || "",
          developmentFee: feeDetails?.developmentFee || "",
          admissionFee: feeDetails?.admissionFee || "",
          sessionFee: feeDetails?.sessionFee || "",
          laboratoryFee: feeDetails?.laboratoryFee || "",
          semesterFee: feeDetails?.semesterFee || "",
          libraryFee: feeDetails?.libraryFee || "",
          gymkhanaFee: feeDetails?.gymkhanaFee || "",
          studyTour: feeDetails?.studyTour || "",
          enrollmentFee: feeDetails?.enrollmentFee || "",
          otherFee: feeDetails?.otherFee || "",
          projectFee: feeDetails?.projectFee || "",
        },
        drlContent: activeDrl,
        // "drlContent": "package com.dbt.drools.rules;\r\n\r\nimport com.dbt.drools.model.Student;\r\nimport com.dbt.drools.model.Benefits;\r\nimport java.util.HashMap;\r\n\r\n\r\nrule \"DHE RCSMSS - All - Female\"\r\n    when\r\n        $s : Student(gender == \"Female\", annualIncome <= 800000)\r\n        $b : Benefits()\r\n    then\r\n\t\tSystem.out.println(\"DHE RCSMSS - Professional - Male - Mid Income \" );\r\n        $b.setTuitionFeeCoveragePercentage(100.0);\r\n        $b.setExamFeeCoveragePercentage(100.0);\r\n        update($b);\r\nend\r\n\r\nrule \"DHE RCSMSS - nonProfessional Course - Male - Upto 800000 Income\"\r\n    when\r\n        $s : Student(professionalCourse == false, gender == \"Male\", annualIncome <= 800000,\r\n                     category in (\"General\", \"SEBC\", \"Open (EWS)\"))\r\n        $b : Benefits()\r\n    then\r\n\t\tSystem.out.println(\"DHE RCSMSS - nonProfessional - Male - Low Income \" );\r\n        \r\n        $b.setTuitionFeeCoveragePercentage(100.0);\r\n        \r\n        $b.setExamFeeCoveragePercentage(100.0);\r\n        update($b);\r\nend\r\n\r\nrule \"DHE RCSMSS - Professional Course - Male - Low Income\"\r\n    when\r\n        $s : Student(professionalCourse == true, gender == \"Male\", annualIncome <= 250000,\r\n                     category in (\"General\", \"SEBC\", \"Open (EWS)\"))\r\n        $b : Benefits()\r\n    then\r\n\t\tSystem.out.println(\"DHE RCSMSS - Professional - Male - Low Income \" );\r\n        if($s.getInstituteType().equals(\"Government\") || $s.getInstituteType().equals(\"Aided\")) {\r\n            $b.setTuitionFeeCoveragePercentage(100.0);\r\n        } else {\r\n            $b.setTuitionFeeCoveragePercentage(50.0);\r\n        }\r\n        $b.setExamFeeCoveragePercentage(50.0);\r\n        update($b);\r\nend\r\n\r\nrule \"DHE RCSMSS - Professional Course - Male - Mid Income\"\r\n    when\r\n        $s : Student(professionalCourse == true, gender == \"Male\", annualIncome > 250000 && annualIncome <= 800000,\r\n                     category in (\"General\", \"SEBC\", \"Open (EWS)\"))\r\n        $b : Benefits()\r\n    then\r\n\t\tSystem.out.println(\"DHE RCSMSS - Professional - Male - Mid Income \" );\r\n        $b.setTuitionFeeCoveragePercentage(50.0);\r\n        $b.setExamFeeCoveragePercentage(50.0);\r\n        update($b);\r\nend\r\n\r\nrule \"DHE RCSMSS - Final Amount Calculation\"\r\n    salience -100\r\n    when\r\n        $s : Student($tFee : tuitionFee, $eFee : examFee)\r\n        $b : Benefits(tuitionFeeCoveragePercentage > 0 || examFeeCoveragePercentage > 0)\r\n    then\r\n        double tBenefit = ($tFee * $b.getTuitionFeeCoveragePercentage()) \/ 100.0;\r\n        double eBenefit = ($eFee * $b.getExamFeeCoveragePercentage()) \/ 100.0;\r\n        \r\n        $b.setTuitionBenefit(tBenefit);\r\n        $b.setExamBenefit(eBenefit);\r\n\t\tdouble total = tBenefit+eBenefit;\r\n        $b.setTotalAmount(total);\r\n\t\tHashMap<String, Double> instMap = new HashMap<>();\r\n        if (total > 0) {\r\n            instMap.put(\"Installment 1\", total * 0.5);\r\n            instMap.put(\"Installment 2\", total * 0.5);\r\n        }\r\n        $b.setInstallments(instMap);\r\n        \r\n        System.out.println(\"RCSMSS Applied: Total Benefit = \" + $b.getTotalAmount());\r\nend"
      };

      console.log("Payload ::: ", payload);

      const response = await fetchBenefitsCalculation(payload);
      const normalizedBenefits = normalizeBenefitsData(response);
      setBenefitsResult(normalizedBenefits);
      setShowModal(true);

      console.log("API Response ::: ", response);
      console.log("Normalized Benefits ::: ", normalizedBenefits);

      // TODO: store response in state to display
    } catch (error) {
      console.error("Error submitting benefits:", error);
      setSubmitError(error?.message || "Failed to calculate benefits.");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setBenefitsResult(null);
    // setFormData(initialForm);
  };

  const selectedDepartmentOption = useMemo(
    () =>
      (options.departments || []).find(
        (department) =>
          String(department?.value || "") === String(formData.departmentName || "")
      ) || null,
    [formData.departmentName, options.departments]
  );
  const selectedDepartmentCode = String(selectedDepartmentOption?.code || "")
    .trim()
    .toUpperCase();

  useEffect(() => {
    let isMounted = true;

    async function loadSchemes() {
      if (!formData.departmentName) {
        setSchemeOptions([]);
        setSelectedDrl("");
        return;
      }

      try {
        setSchemeLoading(true);

        const data = await fetchSchemeConfiguratorOptions(
          formData.departmentName
        );

        if (isMounted) {
          setSchemeOptions(data);
          if (formData.schemeName) {
            const latestDrl = unescapeString(
              data.find(
                (item) =>
                  String(item?.value || "") === String(formData.schemeName || "")
              )?.drl || ""
            );
            setSelectedDrl(latestDrl);
          } else {
            setSelectedDrl("");
          }
        }
      } catch (error) {
        console.error("Failed to load schemes", error);
        if (isMounted) {
          setSchemeOptions([]);
          setSelectedDrl("");
        }
      } finally {
        if (isMounted) {
          setSchemeLoading(false);
        }
      }
    }

    loadSchemes();

    return () => {
      isMounted = false;
    };
  }, [formData.departmentName, formData.schemeName]);

  useEffect(() => {
    let isMounted = true;

    async function loadDistricts() {
      if (!formData.instituteLocation) {
        setOptions((current) => ({
          ...current,
          districts: [],
          talukas: [],
          colleges: [],
          courses: [],
        }));
        return;
      }

      try {
        setLoading((current) => ({ ...current, districts: true }));
        const districts = await fetchDistrictOptionsByState(
          formData.instituteLocation
        );

        if (!isMounted) {
          return;
        }

        setOptions((current) => ({
          ...current,
          districts,
          talukas: [],
          colleges: [],
          courses: [],
        }));
      } catch (error) {
        console.error("Failed to load districts:", error);
        if (isMounted) {
          setOptions((current) => ({
            ...current,
            districts: [],
            talukas: [],
            colleges: [],
            courses: [],
          }));
        }
      } finally {
        if (isMounted) {
          setLoading((current) => ({ ...current, districts: false }));
        }
      }
    }

    loadDistricts();

    return () => {
      isMounted = false;
    };
  }, [formData.instituteLocation]);

  useEffect(() => {
    let isMounted = true;

    async function loadTalukas() {
      if (!formData.districtId) {
        setOptions((current) => ({
          ...current,
          talukas: [],
          colleges: [],
          courses: [],
        }));
        return;
      }

      try {
        setLoading((current) => ({ ...current, talukas: true }));
        const talukas = await fetchTalukaOptionsByDistrict(formData.districtId);

        if (!isMounted) {
          return;
        }

        setOptions((current) => ({
          ...current,
          talukas,
        }));
      } catch (error) {
        console.error("Failed to load talukas:", error);
        if (isMounted) {
          setOptions((current) => ({
            ...current,
            talukas: [],
          }));
        }
      } finally {
        if (isMounted) {
          setLoading((current) => ({ ...current, talukas: false }));
        }
      }
    }

    loadTalukas();

    return () => {
      isMounted = false;
    };
  }, [formData.districtId]);

  useEffect(() => {
    let isMounted = true;

    async function loadColleges() {
      if (!formData.districtId) {
        setOptions((current) => ({
          ...current,
          colleges: [],
          courses: [],
        }));
        return;
      }

      try {
        setLoading((current) => ({ ...current, colleges: true }));
        setOptions((current) => ({
          ...current,
          colleges: [],
          courses: [],
        }));
        const colleges = await fetchCollegeOptionsByTaluka({
          stateId: formData.instituteLocation,
          districtId: formData.districtId,
          talukaId: formData.talukaId,
          departmentCode: selectedDepartmentCode,
        });

        if (!isMounted) {
          return;
        }

        setOptions((current) => ({
          ...current,
          colleges,
          courses: [],
        }));
        setFormData((current) => {
          if (!current.collegeId) {
            return current;
          }

          const selectedCollege = colleges.find(
            (item) => String(item?.value || "") === String(current.collegeId || "")
          );

          if (!selectedCollege) {
            return {
              ...current,
              collegeId: "",
              courseId: "",
              courseType: "",
              instituteType: "",
            };
          }

          const nextInstituteType = getInstituteTypeLabelFromCollege(selectedCollege);

          if (!nextInstituteType) {
            return current;
          }

          if (current.instituteType === nextInstituteType) {
            return current;
          }

          return {
            ...current,
            instituteType: nextInstituteType,
          };
        });
      } catch (error) {
        console.error("Failed to load colleges:", error);
        if (isMounted) {
          setOptions((current) => ({
            ...current,
            colleges: [],
            courses: [],
          }));
        }
      } finally {
        if (isMounted) {
          setLoading((current) => ({ ...current, colleges: false }));
        }
      }
    }

    loadColleges();

    return () => {
      isMounted = false;
    };
  }, [
    formData.instituteLocation,
    formData.districtId,
    formData.talukaId,
    selectedDepartmentCode,
  ]);

  useEffect(() => {
    let isMounted = true;

    async function loadCourses() {
      if (!formData.collegeId) {
        setOptions((current) => ({
          ...current,
          courses: [],
        }));
        return;
      }

      try {
        setLoading((current) => ({ ...current, courses: true }));
        const courses = await fetchCoursesForCollegeId(formData.collegeId);

        if (!isMounted) {
          return;
        }

        setOptions((current) => ({
          ...current,
          courses,
        }));
        setFormData((current) => {
          if (!current.courseId) {
            return current;
          }

          const selectedCourse = courses.find(
            (item) => String(item?.value || "") === String(current.courseId || "")
          );

          if (!selectedCourse) {
            return {
              ...current,
              courseId: "",
              courseType: "",
            };
          }

          const nextCourseType = getCourseTypeLabelFromCourse(selectedCourse);

          if (!nextCourseType) {
            return current;
          }

          if (current.courseType === nextCourseType) {
            return current;
          }

          return {
            ...current,
            courseType: nextCourseType,
          };
        });
      } catch (error) {
        console.error("Failed to load courses:", error);
        if (isMounted) {
          setOptions((current) => ({
            ...current,
            courses: [],
          }));
        }
      } finally {
        if (isMounted) {
          setLoading((current) => ({ ...current, courses: false }));
        }
      }
    }

    loadCourses();

    return () => {
      isMounted = false;
    };
  }, [formData.collegeId]);

  useEffect(() => {
    let isMounted = true;

    async function loadInitialOptions() {
      const masterLoaders = [
        {
          key: "departments",
          label: "Department Name",
          loader: fetchDepartmentOptions,
        },
        {
          key: "schemeTypes",
          label: "Scheme Name",
          loader: fetchSchemeTypeOptions,
        },
        {
          key: "casteCategories",
          label: "Caste Category",
          loader: fetchCasteCategoryOptions,
        },
        {
          key: "beneficiaryCategories",
          label: "Beneficiary Category",
          loader: fetchBeneficiaryCategoryOptions,
        },
        {
          key: "genders",
          label: "Gender",
          loader: fetchGenderOptions,
        },
        {
          key: "religions",
          label: "Religion",
          loader: fetchReligionOptions,
        },
        {
          key: "courseTypes",
          label: "Course Type",
          loader: fetchCourseTypePicklist,
        },
        {
          key: "instituteTypes",
          label: "Institute Type",
          loader: fetchInstituteTypeOptions,
        },
        {
          key: "instituteLocations",
          label: "Institute Location",
          loader: fetchStateOptions,
        },
      ];

      try {
        setLoading((current) => ({ ...current, initial: true }));
        setLoadError("");

        const results = await Promise.allSettled(
          masterLoaders.map(({ loader }) => loader())
        );

        if (!isMounted) {
          return;
        }

        const nextOptions = {};
        const failedFields = [];

        results.forEach((result, index) => {
          const { key, label } = masterLoaders[index];

          if (result.status === "fulfilled") {
            nextOptions[key] = result.value;
            return;
          }

          console.error(`Failed to load ${key}:`, result.reason);
          nextOptions[key] = [];
          failedFields.push(label);
        });

        setOptions((current) => ({
          ...current,
          ...nextOptions,
        }));

        if (failedFields.length > 0) {
          setLoadError(
            `${failedFields.join(", ")} could not be loaded right now. Available fields can still be used.`
          );
        }
      } finally {
        if (isMounted) {
          setLoading((current) => ({ ...current, initial: false }));
        }
      }
    }

    loadInitialOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  const isInitialLoading = loading.initial;
  const isSchemeDisabled = !formData.departmentName;

  const departmentSchemeOptions = useMemo(() => {
    if (!formData.departmentName) {
      return [];
    }

    if (!selectedDepartmentOption?.typeofscheme) {
      return [];
    }

    const schemeTypeMap = new Map(
      (options.schemeTypes || []).map((item) => [item.code, item])
    );

    const seenCodes = new Set();

    return selectedDepartmentOption.typeofscheme
      .split(",")
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean)
      .filter((code) => {
        if (seenCodes.has(code)) {
          return false;
        }

        seenCodes.add(code);
        return true;
      })
      .map((code) => {
        const matchingSchemeType = schemeTypeMap.get(code);

        return {
          value: matchingSchemeType?.value || code,
          label: matchingSchemeType?.label || code,
          code,
        };
      });
  }, [formData.departmentName, options.schemeTypes, selectedDepartmentOption]);

  const helperText = useMemo(() => {
    if (loading.initial) {
      return "Loading master data for the form...";
    }

    if (loadError) {
      return loadError;
    }

    return "Dropdown values are now wired to master APIs. Submit logic will be added next.";
  }, [loadError, loading.initial]);

  const selectedCollegeOption = useMemo(
    () =>
      (options.colleges || []).find(
        (item) => String(item?.value || "") === String(formData.collegeId || "")
      ) || null,
    [formData.collegeId, options.colleges]
  );

  const selectedCourseOption = useMemo(
    () =>
      (options.courses || []).find(
        (item) => String(item?.value || "") === String(formData.courseId || "")
      ) || null,
    [formData.courseId, options.courses]
  );

  const collegeDisplayOptions = useMemo(
    () =>
      (options.colleges || []).map((college) => ({
        ...college,
        label: formatCollegeDisplayLabel(college),
      })),
    [options.colleges]
  );

  const apiInstituteType = getInstituteTypeLabelFromCollege(selectedCollegeOption);
  const apiCourseType = getCourseTypeLabelFromCourse(selectedCourseOption);
  const hasApiInstituteType = Boolean(apiInstituteType);
  const hasApiCourseType = Boolean(apiCourseType);

  const renderSelectField = (name, label, fieldOptions, config = {}) => (
    <div className="kyb-field">
      <div className="kyb-floating-card">
        <label className="kyb-floating-label" htmlFor={name}>
          {label} <span>*</span>
        </label>
        <select
          id={name}
          name={name}
          className="kyb-floating-select"
          value={formData[name]}
          onChange={handleChange}
          disabled={config.disabled}
        >
          <option value="">
            {config.placeholder || `Select ${label}`}
          </option>
          {fieldOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderReadonlyField = (name, label, value, config = {}) => (
    <div className="kyb-field">
      <div className="kyb-floating-card">
        <label className="kyb-floating-label" htmlFor={name}>
          {label} <span>*</span>
        </label>
        <input
          id={name}
          name={name}
          type="text"
          className="kyb-floating-input"
          value={value || ""}
          placeholder={config.placeholder || `Select related value first`}
          readOnly
        />
      </div>
    </div>
  );

  return (
    <div className="kyb-page">
      <div className="kyb-shell container mb-5">
        <div className="kyb-hero bg-white"> 
          <p className="kyb-subtitle">
            Fill in the applicant details below to preview matching scheme
            benefits.
          </p>
        </div>

        <div className="kyb-card">
          <div className="kyb-card-header">
            <div>
              <h5 class="mb-0">Applicant Details</h5>
              <p>Select the relevant details to continue</p>
            </div>
            {/* <span className="kyb-badge">UI Preview</span> */}
          </div>

          <div className="kyb-card-body">
            <div className="kyb-grid">
              {renderSelectField(
                "departmentName",
                "Department Name",
                options.departments,
                {
                  disabled: isInitialLoading,
                }
              )}
              {renderSelectField(
                "schemeName",
                "Scheme Name",
                schemeOptions,
                {
                  disabled: isSchemeDisabled || schemeLoading,
                  placeholder: formData.departmentName
                    ? schemeLoading
                      ? "Loading schemes..."
                      : "Select Scheme Name"
                    : "Select Department First",
                }
              )}

              <div className="kyb-field">
                <div className="kyb-floating-card">
                  <label
                    className="kyb-floating-label"
                    htmlFor="incomeSlab"
                  >
                    Annual Income <span>*</span>
                  </label>
                  <input
                    id="incomeSlab"
                    name="incomeSlab"
                    type="text"
                    className="kyb-floating-input"
                    placeholder="Enter annual income"
                    value={formData.incomeSlab}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {renderSelectField(
                "casteCategory",
                "Caste Category",
                options.casteCategories,
                {
                  disabled: isInitialLoading,
                }
              )}
              {renderSelectField(
                "beneficiaryCategory",
                "Beneficiary Category",
                options.beneficiaryCategories,
                {
                  disabled: isInitialLoading,
                }
              )}
              {renderSelectField("gender", "Gender", options.genders, {
                disabled: isInitialLoading,
              })}
              {renderSelectField("religion", "Religion", options.religions, {
                disabled: isInitialLoading,
              })}
              {renderSelectField(
                "instituteLocation",
                "Institute Location",
                options.instituteLocations,
                {
                  disabled: isInitialLoading,
                  placeholder: "Select State",
                }
              )}
              {renderSelectField(
                "districtId",
                "Institute District",
                options.districts,
                {
                  disabled:
                    isInitialLoading ||
                    loading.districts ||
                    !formData.instituteLocation,
                  placeholder: formData.instituteLocation
                    ? loading.districts
                      ? "Loading districts..."
                      : "Select Institute District"
                    : "Select state first",
                }
              )}
              {renderSelectField(
                "talukaId",
                "Institute Taluka",
                options.talukas,
                {
                  disabled:
                    isInitialLoading ||
                    loading.talukas ||
                    !formData.districtId,
                  placeholder: formData.districtId
                    ? loading.talukas
                      ? "Loading talukas..."
                      : "Select Institute Taluka"
                    : "Select institute district first",
                }
              )}
              {renderSelectField(
                "collegeId",
                "Institute",
                collegeDisplayOptions,
                {
                  disabled:
                    isInitialLoading ||
                    loading.colleges ||
                    !formData.districtId,
                  placeholder: formData.districtId
                    ? loading.colleges
                      ? "Loading colleges..."
                      : "Select Institute"
                    : "Select institute district first",
                }
              )}
              {renderSelectField(
                "courseId",
                "Institute Course",
                options.courses,
                {
                  disabled:
                    isInitialLoading ||
                    loading.courses ||
                    !formData.collegeId,
                  placeholder: formData.collegeId
                    ? loading.courses
                      ? "Loading courses..."
                      : "Select Institute Course"
                    : "Select institute first",
                }
              )}
              {hasApiCourseType
                ? renderReadonlyField(
                    "courseType",
                    "Course Type",
                    apiCourseType || formData.courseType,
                    {
                      placeholder: formData.courseId
                        ? "Waiting for selected course details"
                        : "Select Institute Course first",
                    }
                  )
                : renderSelectField(
                    "courseType",
                    "Course Type",
                    options.courseTypes,
                    {
                      disabled: isInitialLoading || !formData.courseId,
                      placeholder: formData.courseId
                        ? "Select Course Type"
                        : "Select Institute Course first",
                    }
                  )}
              {hasApiInstituteType
                ? renderReadonlyField(
                    "instituteType",
                    "Institute Type",
                    apiInstituteType || formData.instituteType,
                    {
                      placeholder: formData.collegeId
                        ? "Waiting for selected institute details"
                        : "Select Institute first",
                    }
                  )
                : renderSelectField(
                    "instituteType",
                    "Institute Type",
                    options.instituteTypes,
                    {
                      disabled: isInitialLoading || !formData.collegeId,
                      placeholder: formData.collegeId
                        ? "Select Institute Type"
                        : "Select Institute first",
                    }
                  )}
            </div>

            {/* <div
              className={`kyb-footer-note${
                loadError ? " kyb-footer-note-error" : ""
              }`}
            >
              {helperText}
            </div> */}

            {/* <div className="kyb-actions">
              <button
                type="button"
                className="kyb-button kyb-button-secondary"
                onClick={handleReset}
              >
                Reset 
              </button>
              <button type="button" className="kyb-button kyb-button-primary">
                Find Benefits
              </button>
            </div> */}
            {submitError && (
              <div className="alert alert-danger mt-3 mb-0" role="alert">
                {submitError}
              </div>
            )}
            <div className="mt-3 d-flex justify-content-end gap-3 border-top pt-3">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleReset}
              >
                Reset
              </button>
              <button type="button" className="btn btn-primary"
                onClick={handleSubmitBenefits}>
                Find Benefits
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-body">

        {/* {showModal && benefitsResult && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">

                <div className="modal-header">
                  <h5 className="modal-title">Benefits Details</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModal}
                  ></button>
                </div>

                <div className="modal-body">

                  {benefitsResult?.feeBenefits && (
                    <>
                      <h6>Fee Benefits</h6>
                      <table className="table table-bordered">
                        <tbody>
                          {Object.entries(benefitsResult.feeBenefits).map(
                            ([key, value], index) => (
                              <tr key={index}>
                                <th>{key}</th>
                                <td>{value}</td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </>
                  )}

                  {benefitsResult?.additionalBenefits && (
                    <>
                      <h6>Additional Benefits</h6>
                      <table className="table table-bordered">
                        <tbody>
                          {Object.entries(benefitsResult.additionalBenefits).map(
                            ([key, value], index) => (
                              <tr key={index}>
                                <th>{key}</th>
                                <td>{value}</td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </>
                  )}

                 
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                  >
                    Close
                  </button>
                </div>

              </div>
            </div>
          </div>
        )} */}

        {showModal && benefitsResult && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">

                {/* Header */}
                <div className="modal-header">
                  <h5 className="modal-title">Benefits Details</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModal}
                  ></button>
                </div>

                <div className="modal-body">
                  <h6 className="mb-3">Indicative Benefit Calculation Summary</h6>
                  <div className="table-responsive">
                    <table className="table table-bordered table-striped align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th scope="col">Particular</th>
                          <th scope="col" className="text-end">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(benefitsResult.feeBenefits || {}).length > 0 && (
                          <tr className="table-secondary">
                            <th colSpan="2">{BENEFIT_SECTION_LABELS.feeBenefits}</th>
                          </tr>
                        )}

                        {Object.entries(benefitsResult.feeBenefits || {}).map(([key, value]) => (
                          <tr key={key}>
                            <td>{formatBenefitLabel(key)}</td>
                            <td className="text-end">{formatBenefitValue(key, value)}</td>
                          </tr>
                        ))}

                        {Object.entries(benefitsResult.additionalBenefits || {}).length > 0 && (
                          <tr className="table-secondary">
                            <th colSpan="2">{BENEFIT_SECTION_LABELS.additionalBenefits}</th>
                          </tr>
                        )}

                        {Object.entries(benefitsResult.additionalBenefits || {}).map(([key, value]) => (
                          <tr key={key}>
                            <td>{formatBenefitLabel(key)}</td>
                            <td className="text-end">{formatBenefitValue(key, value)}</td>
                          </tr>
                        ))}

                        <tr className="table-secondary">
                          <th colSpan="2">{BENEFIT_SECTION_LABELS.totalAmount}</th>
                        </tr>
                        <tr>
                          <td>{BENEFIT_SECTION_LABELS.totalAmount}</td>
                          <td className="text-end">
                            {formatBenefitValue("totalAmount", benefitsResult.totalAmount)}
                          </td>
                        </tr>

                        {Object.entries(benefitsResult.installments || {}).length > 0 && (
                          <tr className="table-secondary">
                            <th colSpan="2">{BENEFIT_SECTION_LABELS.installments}</th>
                          </tr>
                        )}

                        {Object.entries(benefitsResult.installments || {}).map(([key, value]) => (
                          <tr key={key}>
                            <td>{formatBenefitLabel(key)}</td>
                            <td className="text-end">{formatBenefitValue(key, value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                  >
                    Close
                  </button>
                </div>

              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );



}

export default KnowYourBenefitsForm;
